/**
 * Archive: an admin hides a registry row whose bundle is gone — docs/app/avd-registry.md § Archiving a removed desktop's row.
 * Pure, so the route and the admin page apply the same rule.
 */

import { bundleLifecyclePath, withBundleId, type BundleArchiveFacts } from "./bundle";

export const BUNDLE_ARCHIVE_REASON_MAX_LENGTH = 500;

/** The body of an archive request. The reason is required: an archive is always explained. */
export type BundleArchiveRequest = { reason: string };

/** Response of the archive PUT and DELETE: the row's archive facts after the write. */
export type BundleArchiveResponse = { archive: BundleArchiveFacts | null };

/**
 * Whether a row in this status may be archived: only one with no bundle behind it. A live, failed or
 * in-flight bundle is never hidden.
 */
export function canArchive(status: string): boolean {
	return status === "absent";
}

/** Whether a row is out of the estate: archived and still without a desktop. */
export function isArchived(item: BundleArchiveFacts & { status: string }): boolean {
	return Boolean(item.archivedAt) && canArchive(item.status);
}

/** Why an archive request is invalid; empty = valid. */
export function bundleArchiveRequestErrors(body: unknown): string[] {
	const reason = (body as Partial<BundleArchiveRequest> | undefined)?.reason;
	if (typeof reason !== "string" || reason.trim() === "") return ["reason is required"];
	return reason.trim().length > BUNDLE_ARCHIVE_REASON_MAX_LENGTH
		? [`reason must be at most ${BUNDLE_ARCHIVE_REASON_MAX_LENGTH} characters`]
		: [];
}

/** Admin archive path for one bundle. */
export function bundleArchivePath(bundleType: string, slug: string, bundleId?: string): string {
	return withBundleId(
		`${bundleLifecyclePath(bundleType)}/${encodeURIComponent(slug)}/archive`,
		bundleId,
	);
}
